import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, DOBInput, FieldWrapper, Form, RecoveryPinInput, Text } from 'components';
import { theme, toRem } from 'design';
import Illustration from 'images/illustrations/Security.svg';
import { isValidDateOfBirth, validateFormat } from 'lib/Validations';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { selectCurrentUser, updateDateOfBirthRequest, User } from 'store/User';
import { formatDateForApi } from 'utils/date';
import { AccountSetupContent, AccountSetupFooter, AccountSetupHeader } from './components';

const StyledAccountSetupFooter = styled(AccountSetupFooter)`
  padding: ${toRem(theme.space.lg)};
`;

const AccountSetupAddDateOfBirth = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser());
  const DOBLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_DATE_OF_BIRTH_REQUEST));

  if (currentUser.dateOfBirth && currentUser.dateOfBirth !== '' && currentUser.hasPin) {
    history.replace(routeConstants.private.rootPath);
  }

  let validationSchema = {};

  if (!currentUser.dateOfBirth || currentUser.dateOfBirth === '') {
    validationSchema = {
      ...validationSchema,
      dateOfBirth: Yup.string()
        .test('isValidDateOfBirth', 'Invalid date of birth', (dateOfBirth: string) => {
          if (validateFormat(dateOfBirth, 'MM-DD-YYYY')) {
            return isValidDateOfBirth(formatDateForApi(dateOfBirth));
          }
          return false;
        })
        .required(),
    };
  }

  if (!currentUser?.hasPin) {
    validationSchema = {
      ...validationSchema,
      recoveryPin: Yup.string().required('A 4 digit code must be entered').min(4, 'A 4 digit code must be entered').max(4, 'A 4 digit code must be entered'),
    };
  }

  const validateField = (field: 'dateOfBirth' | 'recoveryPin') => Boolean(touched[field] && errors[field]);
  const { values, errors, touched, isValid, dirty, handleSubmit, getFieldProps } = useFormik({
    initialValues: {
      dateOfBirth: '',
      recoveryPin: '',
    },
    onSubmit: () => {
      const payload = {
        ...(!currentUser?.hasPin && values.recoveryPin && { recoveryPin: values.recoveryPin }),
        ...((!currentUser?.dateOfBirth || currentUser?.dateOfBirth === '') && values.dateOfBirth && { dateOfBirth: formatDateForApi(values.dateOfBirth) }),
      };

      dispatch(updateDateOfBirthRequest(payload, 'Account updated successfully'));
    },
    validateOnBlur: true,
    validationSchema: Yup.object().shape(validationSchema),
    enableReinitialize: true,
  });

  return (
    <AccountSetupContent style={{ gridGap: toRem(16) }}>
      <AccountSetupHeader title="Account Security" />
      <Text>Before you can start using the new Kickfin, let&apos;s make sure your account is secure and setup.</Text>
      <img src={Illustration} alt="Account Setup Complete" style={{ maxHeight: toRem(200) }} />
      <Text size="small">
        Kickfin now requires a bit of additional information to add an extra layer of security. The information provided below will help you recover your account if you have forgotten your password or no longer have access to your mobile
        phone number.
      </Text>
      <Text size="small"> Please use the form below to update your account.</Text>
      <Form onSubmit={handleSubmit}>
        <>
          {(!currentUser.dateOfBirth || currentUser.dateOfBirth === '') && (
            <FieldWrapper label="Date of Birth" message={validateField('dateOfBirth') ? errors.dateOfBirth : ''} hasError={validateField('dateOfBirth')}>
              <DOBInput id="dateOfBirth" disabled={DOBLoading} type="text" placeholder="MM-DD-YYYY" {...getFieldProps('dateOfBirth')} />
            </FieldWrapper>
          )}

          {!currentUser.hasPin && (
            <FieldWrapper data-cy="recovery-pin-field" label="Recovery Pin" message={validateField('recoveryPin') ? errors.recoveryPin : ''} hasError={validateField('recoveryPin')}>
              <RecoveryPinInput id="recoveryPin" disabled={DOBLoading} type="text" placeholder="4 digit pin code" {...getFieldProps('recoveryPin')} />
            </FieldWrapper>
          )}

          <StyledAccountSetupFooter>
            <Button label="Update Your Account" data-cy="submit-button" type="submit" appearance="primary" size="large" disabled={DOBLoading || !(isValid && dirty)} isProcessing={DOBLoading} />
          </StyledAccountSetupFooter>
        </>
      </Form>
    </AccountSetupContent>
  );
};

export default AccountSetupAddDateOfBirth;
